import React, { useRef, useState } from "react";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, FileText } from "lucide-react";

// Anonymous upload for public forms (inspiration / seller photos).
// Stores privately on the server; shows a LOCAL preview (private files aren't publicly fetchable).
// files: array of {id,url,filename,content_type,localPreview}
export default function PublicFileUpload({
  files = [], onChange, label = "Add Photos", accept = "image/*", category = "submission", testId = "public-upload",
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setBusy(true);
    const out = [...files];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      try {
        const r = await publicApi.post("/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push({ ...r.data, localPreview: f.type.startsWith("image") ? URL.createObjectURL(f) : null });
      } catch (err) {
        toast.error("Upload failed: " + (err.response?.data?.detail || f.name));
      }
    }
    setBusy(false);
    onChange(out);
    if (ref.current) ref.current.value = "";
  };

  const remove = (id) => onChange(files.filter((x) => x.id !== id));

  return (
    <div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-3">
          {files.map((f) => (
            <div key={f.id} className="relative">
              {f.localPreview ? (
                <img src={f.localPreview} alt={f.filename} className="h-20 w-20 object-cover rounded border border-[var(--border-2)]" />
              ) : (
                <div className="h-20 w-20 rounded border border-[var(--border-2)] bg-[var(--bg)] flex flex-col items-center justify-center text-[var(--muted)]">
                  <FileText size={20} /><span className="text-[9px] mt-1 px-1 text-center truncate w-full">{f.filename}</span>
                </div>
              )}
              <button type="button" onClick={() => remove(f.id)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <input ref={ref} type="file" accept={accept} multiple className="hidden" onChange={handle} data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testId}-btn`}
        className="border-[var(--border-2)] bg-transparent text-[var(--text-2)] hover:text-[var(--text)] rounded-none">
        <Upload size={15} className="mr-2" />{busy ? "Uploading…" : label}
      </Button>
    </div>
  );
}
